import React from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { EmptyState, LoadingState, SectionCard } from '../ui';
import {
  listStoreSalePolicies,
  listStoreSaleWindows,
  STORE_SALE_POLICIES_QUERY_KEY,
  STORE_SALE_WINDOWS_QUERY_KEY,
  type BrandSalePolicy,
  type StoreSaleWindow,
} from '../../services/saleWindowsApi';
import { saleWindowChip, saleWindowFloorText, saleWindowProductsText, saleWindowRange } from './saleWindowShared';

const NS = 'store.saleWindows';

/**
 * Jordan's view of vendor-approved sale windows: when a brand lets the store
 * advertise below MAP, and down to what. Read-only; inside a window a
 * below-MAP override saves without a MAP block and is not a violation, and
 * the price ends with the window. Hidden while `vendor_sale_windows` is off.
 */
export const StoreSaleWindowsSection: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatCurrency } = useLocale();
  const { enabled } = useOrgFeatureFlag('vendor_sale_windows');

  // The rule is per brand (MOT-388): the card exists only for brands that run it.
  const { data: policies } = useQuery<BrandSalePolicy[]>({
    queryKey: STORE_SALE_POLICIES_QUERY_KEY,
    queryFn: listStoreSalePolicies,
    enabled,
  });
  const brands = policies || [];
  const { data: windows, isLoading, isError } = useQuery<StoreSaleWindow[]>({
    queryKey: STORE_SALE_WINDOWS_QUERY_KEY,
    queryFn: listStoreSaleWindows,
    enabled: enabled && brands.length > 0,
  });

  if (!enabled || brands.length === 0) return null;

  const active = (windows || []).filter((w) => w.status === 'active').length;
  const upcoming = (windows || []).filter((w) => w.status === 'upcoming').length;

  return (
    <Box sx={{ mb: 3 }}>
      <SectionCard dense padded title={t(`${NS}.title`)} subtitle={t(`${NS}.coverage`, { active, upcoming })}>
        {brands.map((b) => (
          <Typography key={b.brandId} variant="body2" sx={{ mb: 0.5 }}>
            <strong>{b.brandName}</strong>: {t(`${NS}.brandRule`)}
          </Typography>
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>{t(`${NS}.intro`)}</Typography>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <Alert severity="error">{t(`${NS}.loadError`)}</Alert>
        ) : !windows || windows.length === 0 ? (
          <EmptyState title={t(`${NS}.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
              {windows.map((w) => (
                <Box key={w.id} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{w.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {w.brandName} · {w.dealerSpecific ? t(`${NS}.forYourStore`) : t(`${NS}.allDealers`)}
                      </Typography>
                    </Box>
                    {saleWindowChip(w.status, t, NS)}
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {saleWindowRange(w.startAt, w.endAt)} · {saleWindowFloorText(w, t, NS, formatCurrency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {saleWindowProductsText(w, t, NS)}
                  </Typography>
                  {w.note && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{w.note}</Typography>}
                  {!w.verified && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                      {t(`${NS}.verifyToPrice`, { brand: w.brandName })}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Paper>
        )}
      </SectionCard>
    </Box>
  );
};
